<template>
  <b-container>
    <div id="control-header" style="padding: 10px; font-size: large; font-weight: bold">
      <b-row>
        <b-col cols="9">
          Fuel Data
        </b-col>
        <b-col cols="3" class="text-right">
          <b-button size="sm" @click="requestDownload()">
            Export Fuel Data
          </b-button>
        </b-col>
      </b-row>
    </div>
    <ag-grid-vue style="width: 100%; height: 400px"
                 class="ag-theme-balham"
                 :gridOptions="topGridOptions"
                 :columnDefs="columnDefs"
                 :rowData="rowData"
                 :defaultColDef="defaultColDef"
                 :enterMovesDownAfterEdit="true"
                 @grid-ready="onGridReady"
                 @cell-clicked="onCellClicked"
                 @cell-editing-stopped="onCellEditingStopped">
    </ag-grid-vue>
    <ag-grid-vue style="width: 100%; height: 40px;"
                 class="ag-theme-balham"
                 :gridOptions="bottomGridOptions"
                 :headerHeight="0"
                 :columnDefs="bottomColumnDefs"
                 :rowData="bottomDataCalc"
                 :rowStyle="rowStyle"
    ></ag-grid-vue>
  </b-container>
</template>

<script>
import {AgGridVue} from "ag-grid-vue";
import NumericEditor from "@/numericEditor";
import Bus from "@/eventBus";
import {roundColumnValue} from "@/MetricUtils";
import {FileUtils} from "@/lib/FileUtils";

const NATURAL_GAS_METER = "naturalGas";
const PROPANE_METER = "propane";
const GAS_METER = "gasoline";
const DIESEL_METER = "diesel";

const SizeEnum = {

  EM1: NATURAL_GAS_METER,
  EM2: PROPANE_METER,
  EM3: GAS_METER,
  EM4: DIESEL_METER
};

window.numberParser = function numberParser(params) {
  let parsedNumber = Number(params.newValue);
  return isNaN(parsedNumber) ? "ERROR" : parsedNumber;
};

export default {
  name: "VineyardGasMetricTable",
  props: {rows: Array, year: String, id: String, groupId: String},
  components: {
    AgGridVue
  },

  data() {
    return {
      metricYearVal: this.year,
      gridApi: null,
      gridColumnApi: null,
      bottomColumnDefs: null,
      columnDefs: null,
      defaultColDef: null,
      pinnedTopRowData: null,
      pinnedBottomRowData: null,
      topGridOptions: null,
      bottomGridOptions: null,
      rowData: this.rows,
      bottomData: [],
      rowStyle: {fontWeight: 'bold'},
      enteredValueDecimalPlaces: 2,
      totalsValueDecimalPlaces: 0,
      metricId: this.id,
      myGroupId: this.groupId,
      isViewer: localStorage.getItem('roles') !== null && localStorage.getItem('roles').includes('VIEWER')
    }
  },
  mounted() {
    this.gridApi = this.topGridOptions.api;
    this.gridColumnApi = this.topGridOptions.columnApi;
    //this.$nextTick(this.sendInitialMetricsUpdate(this.metricYearVal));
  },
  watch: {
    rows: function (newVal, oldVal) {
      this.rowData = newVal;
    },
    year: function (newVal, oldVal) {
      this.metricYearVal = newVal;
    },
    id: function (newVal, oldVal) {
      this.metricId = newVal;
    },
    groupId: function (newVal, oldVal) {
      this.myGroupId = newVal;
    }
  },
  beforeMount() {

    this.columnDefs = [
      {
        headerName: 'Month', field: 'month', pinned: "left",
        lockPinned: true,
        width: 100,
        editable: false
      },
      {
        headerName: 'Year', field: 'year', pinned: "left",
        lockPinned: true,
        width: 75,
        editable: false
      },
      {
        headerName: 'Natural Gas (Therms)',
        field: NATURAL_GAS_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      },
      {
        headerName: 'Propane (Gallons)',
        field: PROPANE_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      },
      {
        headerName: 'Gasoline (Gallons)',
        field: GAS_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      },
      {
        headerName: 'Diesel (Gallons)',
        field: DIESEL_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.enteredValueDecimalPlaces)
      }
    ];
    this.bottomColumnDefs = [
      {
        headerName: 'Month', field: 'month', pinned: "left",
        lockPinned: true,
        width: 100,
        editable: false
      },
      {
        headerName: 'Year', field: 'year', pinned: "left",
        lockPinned: true,
        width: 75,
        editable: false
      },
      {
        headerName: 'Natural Gas (Therms)',
        field: NATURAL_GAS_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      },
      {
        headerName: 'Propane (Gallons)',
        field: PROPANE_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      },
      {
        headerName: 'Gasoline (Gallons)',
        field: GAS_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      },
      {
        headerName: 'Diesel (Gallons)',
        field: DIESEL_METER,
        valueParser: numberParser,
        editable: !this.isViewer,
        valueFormatter: params => roundColumnValue(params.data[params.column.colId], this.totalsValueDecimalPlaces)
      }
    ];

    this.bottomData = [
      {
        month: 'Total',
        year: '******',
        naturalGas: 'a lot',
        propane: 'not as much',
        gasoline: 'a little more',
        diesel: '=ctx.sum("solarProductivity")'
      }
    ];

    this.defaultColDef = {
      editable: true,
      resizable: true
    };

    this.pinnedTopRowData = getPinnedTopData();
    this.pinnedBottomRowData = getPinnedBottomData();

    this.topGridOptions = {alignedGrids: [], suppressHorizontalScroll: true};
    this.bottomGridOptions = {
      alignedGrids: [], enableCellExpressions: true, context: {
        theNumber: 4
      }
    };
    this.frameworkComponents = {
      numericEditor: NumericEditor
    };
    this.topGridOptions.alignedGrids.push(this.bottomGridOptions);
    this.bottomGridOptions.alignedGrids.push(this.topGridOptions);


  },
  computed: {
    bottomDataCalc: function () {

      let self = this;
      let bottomRowsMap = new Map();
      let columnNames = [];
      Object.values(SizeEnum).map(key => {
        let result = 0;
        Object.values(self.rowData).forEach(row => {

          result += row[key];

        });
        columnNames.push(result);
      });
      console.log("Gas Column Names");
      console.log(columnNames);

      let bottom =
          {
            month: "Total",
            year: '******',
            naturalGas: 0,
            propane: 0,
            gasoline: 0,
            diesel: 0
          };
      if (bottomRowsMap !== undefined) {
        bottom = {
          month: "Total",
          year: '******',
          naturalGas: Number(columnNames[0]),
          propane: Number(columnNames[1]),
          gasoline: Number(columnNames[2]),
          diesel: Number(columnNames[3])
        };
      }

      console.log(bottom);

      return [bottom];
    }
  },
  methods: {
    onGridReady(params) {

      this.gridApi = this.topGridOptions.api;
      this.gridColumnApi = this.topGridOptions.columnApi;
      const updateData = data => {
        this.rowData = data;
      };
    },
    onCellClicked(event) {
      //console.log('onCellClicked: ' + event.rowIndex + ' ' + event.colDef.field);

    },
    sendInitialMetricsUpdate: function (year) {
      let naturalGas = 0;
      let propane = 0;
      let gasoline = 0;
      let diesel = 0;
      if (this.gridApi !== undefined) {
        this.gridApi.forEachNode(function (node) {
          if (node.data["year"] === year) {
            naturalGas += Number(node.data["naturalGas"]) || 0;
            propane += Number(node.data["propane"]) || 0;
            gasoline += Number(node.data["gasoline"]) || 0;
            diesel += Number(node.data["diesel"]) || 0;
          }
        });
      }
      //this.calculateAndEmitWaterTotals(naturalGas, propane, gasoline, diesel, event);
      this.calculateAndUpdateGasTotalsVuex(naturalGas, propane, gasoline, diesel, event);
    },
    calculateAndEmitGasTotals: function (naturalGas, propane, gasoline, diesel, event) {
      console.log("Calculating and Emitting Water Totals");
      this.bottomGridOptions.api.getRowNode('0').data["naturalGas"] = naturalGas;
      this.bottomGridOptions.api.getRowNode('0').data["propane"] = propane;
      this.bottomGridOptions.api.getRowNode('0').data["gasoline"] = gasoline;
      this.bottomGridOptions.api.getRowNode('0').data["diesel"] = diesel;

      let theGridType = "vineyardWaterGrid";

      let waterEmission = {
        gridType: theGridType,
        naturalGas: naturalGas,
        propane: propane,
        gasoline: gasoline,
        diesel: diesel,
        year: Number(this.metricYearVal)
      };
      Bus.$emit('vineyard-gas-edited', waterEmission);
      console.log("Emitted Vineyard Gas Event");
    },
    calculateAndUpdateGasTotalsVuex: function (naturalGas, propane, gasoline, diesel, event) {
      console.log("Calculating and Emitting Water Totals");
      this.bottomGridOptions.api.getRowNode('0').data["naturalGas"] = naturalGas;
      this.bottomGridOptions.api.getRowNode('0').data["propane"] = propane;
      this.bottomGridOptions.api.getRowNode('0').data["gasoline"] = gasoline;
      this.bottomGridOptions.api.getRowNode('0').data["diesel"] = diesel;

      let theGridType = "vineyardWaterGrid";

      let waterEmission = {
        gridType: theGridType,
        naturalGas: naturalGas,
        propane: propane,
        gasoline: gasoline,
        diesel: diesel,
        year: Number(this.metricYearVal)
      };
      this.$store.commit('totalNaturalGas', naturalGas);
      this.$store.commit('totalPropane', propane);
      this.$store.commit('totalGasoline', gasoline);
      this.$store.commit('totalDiesel', diesel);
      console.log(`Vuex Vineyard Gas Event Updated: ${waterEmission}`);
    },
    safeParseNumber: function (node, indexName) {
      let isNotANumber = isNaN(node.data[indexName]);
      console.log(`Is Not a Number: ${isNotANumber}, Node Value: ${node.data[indexName]} returned Value: ${isNotANumber ? 0 : node.data[indexName]}`);
      return isNotANumber ? 0 : Number(node.data[indexName]);
    },
    requestDownload() {
      let self = this;
      this.$http.get(`metrics/download/${self.myGroupId}/${self.metricYearVal}/gas/vineyard`, {responseType: "blob"})
          .then(async response => {
            FileUtils.saveExcelToDisk(response);
          });
    },
    onCellEditingStopped(event) {
      console.log('onCellEditingStopped: ' + event.rowIndex + ' ' + event.colId);
      let self = this;
      console.log(event)
      console.log(event.cellIndex)
      console.log(event.colDef.field)

      //let items: Array<rows> = [];
      let naturalGas = 0;
      let propane = 0;
      let gasoline = 0;
      let diesel = 0;
      if (self.gridApi !== undefined) {
        self.gridApi.forEachNode(function (node) {
          if (node.data["year"] === event.data["year"]) {
            naturalGas += self.safeParseNumber(node, "naturalGas");
            propane += self.safeParseNumber(node, "propane");
            gasoline += self.safeParseNumber(node, "gasoline");
            diesel += self.safeParseNumber(node, "diesel");
          }
        });
      }

      //TODO This is where I re-enable Database update
      this.postUpdateToServer();
      // TODO update the items to be posted here!
      this.calculateAndUpdateGasTotalsVuex(naturalGas, propane, gasoline, diesel, event);
      this.bottomGridOptions.api.redrawRows({rowNodes: [this.bottomGridOptions.api.getRowNode('0')]})
    },
    postUpdateToServer: function () {
      let self = this;
      if (self.gridApi !== undefined) {
        let numberOfRows = 0;
        let allPowerMetrics = [];
        let year = {};
        let vineyardGasMetricsMonth = [];
        self.gridApi.forEachNode(function (node) {
          year = node.data["year"];
          vineyardGasMetricsMonth.push({
            month: node.data["month"],
            naturalGas: self.safeParseNumber(node, "naturalGas"),
            propane: self.safeParseNumber(node, "propane"),
            gasoline: self.safeParseNumber(node, "gasoline"),
            diesel: self.safeParseNumber(node, "diesel"),
          })
        });
        let metricYear = {
          _id: this.metricId,
          year: year,
          groupId: this.myGroupId,
          months: vineyardGasMetricsMonth
        }
        console.log("Metrics Year");
        console.log(JSON.stringify(metricYear));
        this.$http.put("/metrics/vineyard/gas/" + this.metricId, JSON.stringify(metricYear), {
          headers: {
            'content-type': 'application/json'
          }
        }).then(response => {
          console.log(response);
          if (self.metricId === null) {
            self.metricId = response.data;
          }
        }).catch(error => {
          console.log("Error Putting Data");
          console.error(error);
        });
      }
    }
  }
}

function getPinnedTopData() {
  return [
    {
      firstName: "##",
      lastName: "##",
      gender: "##",
      address: "##",
      mood: "##",
      country: "##"
    }
  ];
}

function getPinnedBottomData() {
  return [
    {
      firstName: "##",
      lastName: "##",
      gender: "##",
      address: "##",
      mood: "##",
      country: "##"
    }
  ];
}
</script>

<style scoped>

</style>
